import { useCallback, useMemo } from "react";
import { filter, orderBy, toNumber, uniqBy } from "lodash";
import {
  useAssessmentQuery,
  Assessment,
} from "@/features/assessments/graphql/generated";
import {
  useEvaluationsQuery,
  useEvaluatePersonMutation,
  EvaluatePersonMutationVariables,
  AssessmentEvaluation,
} from "@/features/people/graphql/generated";
import { useViewer } from "@/features/profile/hooks/useViewer";
import { useAccount } from "@/features/workspace/hooks/useAccount";

interface EvaluationArgs {
  evaluation: number;
  rowId: number;
  personId?: number;
  meta?: Record<string, any>;
}
export const useAssessment = (assessmentId: number, _personId?: number) => {
  const { authorizedPersonId } = useViewer();
  const { account } = useAccount();
  const { data, loading } = useAssessmentQuery({
    variables: {
      id: assessmentId,
    },
    skip: !assessmentId,
  });

  const personId = _personId || data?.assessment?.person?.id;

  const { data: evaluationsData, loading: evaluationsLoading } =
    useEvaluationsQuery({
      variables: {
        assessmentId,
        personId: toNumber(personId),
        projectId: null,
      },
      skip: !personId || !assessmentId,
    });

  const [evaluatePersonMutation, {}] = useEvaluatePersonMutation({
    refetchQueries: ["Evaluations"],
  });

  const handePersonEvaluation = useCallback(
    async (args: EvaluationArgs) => {
      const result = await evaluatePersonMutation({
        variables: {
          assessmentId,
          ...args,
          personId: toNumber(personId) || args.personId || null,
        } as EvaluatePersonMutationVariables,
      });

      return result.data?.evaluatePerson;
    },
    [assessmentId]
  );

  const assessment = useMemo(() => {
    const assessmentBase = data?.assessment || ({} as Assessment);

    const orderedRows = orderBy(
      assessmentBase.scorecard?.rows,
      ["id"],
      ["asc"]
    );

    return {
      ...assessmentBase,
      scorecard: {
        ...assessmentBase.scorecard,
        rows: orderedRows,
      },
    } as Assessment;
  }, [data?.assessment]);

  const evaluations = useMemo(() => {
    if (account?.config?.single_score_evaluations) {
      return uniqBy(
        orderBy(evaluationsData?.evaluations, "created_by", "desc"),
        "row_id"
      );
    }

    return (
      filter(evaluationsData?.evaluations, {
        created_by: authorizedPersonId,
      }) || ([] as AssessmentEvaluation[])
    );
  }, [evaluationsData, authorizedPersonId, account.config]);

  return {
    assessment,
    evaluations,
    loading,
    handePersonEvaluation,
  };
};
